import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { register, resendVerificationEmail } from "../services/authService";
import { Footer } from "../components/layout/Footer";

const SignUpPage: React.FC = () => {
  const navigate = useNavigate();
  const [displayName, setDisplayName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [error, setError] = useState("");
  const [success, setSuccess] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [resendStatus, setResendStatus] = useState<string>("");
  const [resendDisabled, setResendDisabled] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");

    // Validation
    if (!displayName.trim()) {
      setError("Display name is required");
      return;
    }

    if (!email.trim()) {
      setError("Email is required");
      return;
    }

    if (password.length < 8) {
      setError("Password must be at least 8 characters long");
      return;
    }

    if (password !== confirmPassword) {
      setError("Passwords do not match");
      return;
    }

    // Check password strength (match backend requirements)
    const hasUppercase = /[A-Z]/.test(password);
    const hasLowercase = /[a-z]/.test(password);
    const hasDigit = /\d/.test(password);
    const hasSpecial = /[!@#$%^&*(),.?":{}|<>]/.test(password);

    if (!hasUppercase || !hasLowercase || !hasDigit || !hasSpecial) {
      setError(
        "Password must contain at least one uppercase letter, one lowercase letter, one digit, and one special character",
      );
      return;
    }

    setIsLoading(true);

    try {
      await register(email, password, displayName);
      setSuccess(true);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Registration failed");
    } finally {
      setIsLoading(false);
    }
  };

  // Success confirmation page
  if (success) {
    return (
      <div className="min-h-screen flex flex-col">
        <div className="flex-1 flex animated-gradient">
        {/* Left side - Branding/Hero */}
        <div className="hidden lg:flex lg:w-1/2 relative overflow-hidden">
          {/* Video Background */}
          <video
            autoPlay
            loop
            muted
            playsInline
            className="absolute inset-0 w-full h-full object-cover scale-105"
          >
            <source src="/header.mp4?v=2" type="video/mp4" />
          </video>

          {/* Overlay for readability */}
          <div className="absolute inset-0 bg-surface-950/50" />
          <div className="absolute inset-0 bg-gradient-to-br from-primary-950/70 via-surface-950/40 to-accent-900/30" />

          {/* Content */}
          <div className="relative z-10 flex flex-col justify-center p-12">
            {/* Colvix logo */}
            <a
              href="https://www.colvix.net"
              target="_blank"
              rel="noopener noreferrer"
              className="mb-8 opacity-80 hover:opacity-100 transition-opacity inline-block"
            >
              <img
                src="/colvix_header_final.png" alt="Colvix" className="colvix-logo h-8"
              />
            </a>

            {/* TradeCraft branding */}
            <div className="mb-8">
              <div className="flex items-center gap-4 mb-6">
                <img
                  src="/logo.svg"
                  alt="TradeCraft"
                  className="w-16 h-16 drop-shadow-lg"
                />
                <div>
                  <h1 className="text-4xl font-bold gradient-text">
                    TradeCraft
                  </h1>
                  <p className="text-surface-400 text-lg">Algo Builder</p>
                </div>
              </div>
            </div>

            {/* Success features */}
            <div className="space-y-4">
              {[
                {
                  icon: "M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z",
                  title: "Check Your Email",
                  desc: "We sent a verification link",
                },
                {
                  icon: "M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z",
                  title: "Verify Your Account",
                  desc: "Click the link to activate",
                },
                {
                  icon: "M13 7h8m0 0v8m0-8l-8 8-4-4-6 6",
                  title: "Start Trading",
                  desc: "Build and backtest strategies",
                },
              ].map((feature, i) => (
                <div
                  key={i}
                  className="flex items-start gap-4 p-4 rounded-xl bg-surface-900/30 backdrop-blur-sm border border-surface-700/30"
                >
                  <div className="w-10 h-10 rounded-lg bg-primary-500/20 flex items-center justify-center flex-shrink-0">
                    <svg
                      className="w-5 h-5 text-primary-400"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d={feature.icon}
                      />
                    </svg>
                  </div>
                  <div>
                    <h3 className="font-semibold text-surface-100">
                      {feature.title}
                    </h3>
                    <p className="text-sm text-surface-400">{feature.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Decorative elements */}
          <div className="absolute top-20 right-20 w-64 h-64 bg-green-500/10 rounded-full blur-3xl" />
          <div className="absolute bottom-20 left-20 w-48 h-48 bg-accent-500/10 rounded-full blur-3xl" />
        </div>

        {/* Right side - Success message */}
        <div className="w-full lg:w-1/2 flex items-center justify-center p-8">
          <div className="w-full max-w-md">
            {/* Mobile branding */}
            <div className="lg:hidden text-center mb-8">
              <a
                href="https://www.colvix.net"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-block mb-4 opacity-80 hover:opacity-100 transition-opacity"
              >
                <img
                  src="/colvix_header_final.png"
                  alt="Colvix"
                  className="colvix-logo h-6 mx-auto"
                />
              </a>
              <div className="flex items-center justify-center gap-3 mb-2">
                <img
                  src="/logo.svg"
                  alt="TradeCraft"
                  className="w-12 h-12"
                />
                <h1 className="text-2xl font-bold gradient-text">
                  TradeCraft
                </h1>
              </div>
              <p className="text-surface-400">Algo Builder</p>
            </div>

            {/* Success card */}
            <div className="glass-card p-8 animate-slide-up">
              <div className="text-center">
                <div className="mx-auto h-16 w-16 bg-green-500/20 rounded-full flex items-center justify-center mb-6">
                  <svg
                    className="w-8 h-8 text-green-400"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M5 13l4 4L19 7"
                    />
                  </svg>
                </div>
                <h2 className="text-2xl font-bold text-surface-100 mb-2">
                  Account Created!
                </h2>
                <p className="text-surface-400 mb-2">
                  We've sent a verification email to{" "}
                  <span className="text-primary-400 font-medium">{email}</span>.
                </p>
                <p className="text-surface-400 text-sm mb-2">
                  Please check your inbox and click the verification link to
                  activate your account.
                </p>
                <p className="text-yellow-400 text-sm font-bold mb-6">
                  ⚠️ Don't forget to check your Junk/Spam folder!
                </p>
              </div>

              <div className="space-y-4">
                <button
                  onClick={() => navigate("/login")}
                  className="btn-primary w-full"
                >
                  Go to Login
                </button>
                <p className="text-center text-surface-500 text-sm">
                  Didn't receive the email?{" "}
                  <button
                    className={`font-medium ${
                      resendDisabled
                        ? "text-surface-600 cursor-not-allowed"
                        : "text-primary-400 hover:text-primary-300"
                    }`}
                    disabled={resendDisabled}
                    onClick={async () => {
                      setResendDisabled(true);
                      setResendStatus("");
                      try {
                        await resendVerificationEmail(email);
                        setResendStatus("Verification email sent! Check your inbox.");
                      } catch {
                        setResendStatus("Failed to resend. Please try again later.");
                      }
                      setTimeout(() => setResendDisabled(false), 30000);
                    }}
                  >
                    Resend verification
                  </button>
                </p>
                {resendStatus && (
                  <p className={`text-center text-sm mt-2 ${
                    resendStatus.includes("sent") ? "text-green-400" : "text-red-400"
                  }`}>
                    {resendStatus}
                  </p>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
      <Footer />
    </div>
    );
  }

  // Registration form
  return (
    <div className="min-h-screen flex flex-col">
      <div className="flex-1 flex animated-gradient">
      {/* Left side - Branding/Hero */}
      <div className="hidden lg:flex lg:w-1/2 relative overflow-hidden">
        {/* Video Background */}
        <video
          autoPlay
          loop
          muted
          playsInline
          className="absolute inset-0 w-full h-full object-cover scale-105"
        >
          <source src="/header.mp4?v=2" type="video/mp4" />
        </video>

        {/* Overlay for readability */}
        <div className="absolute inset-0 bg-surface-950/50" />
        <div className="absolute inset-0 bg-gradient-to-br from-primary-950/70 via-surface-950/40 to-accent-900/30" />

        {/* Content */}
        <div className="relative z-10 flex flex-col justify-center p-12">
          {/* Colvix logo */}
          <a
            href="https://www.colvix.net"
            target="_blank"
            rel="noopener noreferrer"
            className="mb-8 opacity-80 hover:opacity-100 transition-opacity inline-block"
          >
            <img src="/colvix_header_final.png" alt="Colvix" className="colvix-logo h-8" />
          </a>

          {/* TradeCraft branding */}
          <div className="mb-8">
            <div className="flex items-center gap-4 mb-6">
              <img
                src="/logo.svg"
                alt="TradeCraft"
                className="w-16 h-16 drop-shadow-lg"
              />
              <div>
                <h1 className="text-4xl font-bold gradient-text">
                  TradeCraft
                </h1>
                <p className="text-surface-400 text-lg">Algo Builder</p>
              </div>
            </div>
          </div>

          {/* Features list */}
          <div className="space-y-4">
            {[
              {
                icon: "M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z",
                title: "Comprehensive Backtesting",
                desc: "Test strategies with historical data",
              },
              {
                icon: "M13 7h8m0 0v8m0-8l-8 8-4-4-6 6",
                title: "Performance Analytics",
                desc: "Sharpe ratio, drawdown, and more",
              },
              {
                icon: "M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z",
                title: "Portfolio Management",
                desc: "Track your investment strategies",
              },
            ].map((feature, i) => (
              <div
                key={i}
                className="flex items-start gap-4 p-4 rounded-xl bg-surface-900/30 backdrop-blur-sm border border-surface-700/30"
              >
                <div className="w-10 h-10 rounded-lg bg-primary-500/20 flex items-center justify-center flex-shrink-0">
                  <svg
                    className="w-5 h-5 text-primary-400"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d={feature.icon}
                    />
                  </svg>
                </div>
                <div>
                  <h3 className="font-semibold text-surface-100">
                    {feature.title}
                  </h3>
                  <p className="text-sm text-surface-400">{feature.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Decorative elements */}
        <div className="absolute top-20 right-20 w-64 h-64 bg-primary-500/10 rounded-full blur-3xl" />
        <div className="absolute bottom-20 left-20 w-48 h-48 bg-accent-500/10 rounded-full blur-3xl" />
      </div>

      {/* Right side - Sign up form */}
      <div className="w-full lg:w-1/2 flex items-center justify-center p-8">
        <div className="w-full max-w-md">
          {/* Mobile branding */}
          <div className="lg:hidden text-center mb-8">
            <a
              href="https://www.colvix.net"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-block mb-4 opacity-80 hover:opacity-100 transition-opacity"
            >
              <img
                src="/colvix_header_final.png"
                alt="Colvix"
                className="colvix-logo h-6 mx-auto"
              />
            </a>
            <div className="flex items-center justify-center gap-3 mb-2">
              <img src="/logo.svg" alt="TradeCraft" className="w-12 h-12" />
              <h1 className="text-2xl font-bold gradient-text">
                TradeCraft
              </h1>
            </div>
            <p className="text-surface-400">Algo Builder</p>
          </div>

          {/* Sign up card */}
          <div className="glass-card p-8 animate-slide-up">
            <div className="text-center mb-8">
              <h2 className="text-2xl font-bold text-surface-100">
                Create Account
              </h2>
              <p className="text-surface-400 mt-2">
                Join TradeCraft and start building your portfolio
              </p>
            </div>

            <form onSubmit={handleSubmit} className="space-y-5">
              {error && (
                <div className="bg-red-500/10 border border-red-500/50 rounded-xl p-4 flex items-center gap-3">
                  <svg
                    className="w-5 h-5 text-red-400 flex-shrink-0"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                    />
                  </svg>
                  <span className="text-red-400 text-sm">{error}</span>
                </div>
              )}

              <div>
                <label className="block text-sm font-medium text-surface-300 mb-2">
                  Display Name
                </label>
                <input
                  type="text"
                  value={displayName}
                  onChange={(e) => setDisplayName(e.target.value)}
                  className="input-field w-full"
                  placeholder="John Doe"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-surface-300 mb-2">
                  Email Address
                </label>
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="input-field w-full"
                  placeholder="you@example.com"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-surface-300 mb-2">
                  Password
                </label>
                <input
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="input-field w-full"
                  placeholder="••••••••"
                  required
                />
                <p className="mt-1 text-xs text-surface-500">
                  Min 8 chars with uppercase, lowercase, number & special
                  character
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-surface-300 mb-2">
                  Confirm Password
                </label>
                <input
                  type="password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  className="input-field w-full"
                  placeholder="••••••••"
                  required
                />
              </div>

              <button
                type="submit"
                disabled={isLoading}
                className="btn-primary w-full"
              >
                {isLoading ? (
                  <div className="flex items-center justify-center">
                    <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin mr-2"></div>
                    Creating Account...
                  </div>
                ) : (
                  "Create Account"
                )}
              </button>
            </form>

            <div className="mt-6 text-center">
              <p className="text-surface-400">
                Already have an account?{" "}
                <Link
                  to="/login"
                  className="text-primary-400 hover:text-primary-300 font-medium transition-colors"
                >
                  Sign in
                </Link>
              </p>
            </div>
          </div>

          {/* Terms note */}
          <p className="text-center text-xs text-surface-500 mt-6">
            By creating an account, you agree to our{" "}
            <Link to="/terms" className="text-surface-400 hover:text-surface-300">
              Terms of Service
            </Link>{" "}
            and{" "}
            <Link to="/privacy" className="text-surface-400 hover:text-surface-300">
              Privacy Policy
            </Link>
          </p>
        </div>
      </div>
      </div>
      <Footer />
    </div>
  );
};

export default SignUpPage;
